'use client';
import React, { useState } from 'react';
import { createClient } from '@/lib/supabase/client';

interface IntakeForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  businessName: string;
  businessType: string;
  services: string[];
  frequency: string;
  currentSoftware: string;
  monthsBehind: string;
  message: string;
  consent: boolean;
}

const initialForm: IntakeForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  businessName: '',
  businessType: '',
  services: [],
  frequency: '',
  currentSoftware: '',
  monthsBehind: '',
  message: '',
  consent: false,
};

const serviceOptions = [
  'Bookkeeping',
  'Bank Reconciliation',
  'Financial Statement Preparation',
];

const businessTypes = [
  'Sole Proprietor / Freelancer',
  'LLC (Single Member)',
  'LLC (Multi-Member)',
  'S-Corporation',
  'C-Corporation',
  'Partnership',
  'Non-Profit',
  'Other',
];

const softwareOptions = [
  'QuickBooks Online',
  'QuickBooks Desktop',
  'Xero',
  'Wave',
  'FreshBooks',
  'Excel / Google Sheets',
  'No software currently',
  'Other',
];

const inputStyle = { fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", minHeight: '48px', fontSize: '16px' };
const selectStyle = { fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", minHeight: '48px', fontSize: '16px' };

export default function ClientPortalSection() {
  const [form, setForm] = useState<IntakeForm>(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof IntakeForm, string>>>({});
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field: keyof IntakeForm, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleServiceToggle = (service: string) => {
    setForm((prev) => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter((s) => s !== service)
        : [...prev.services, service],
    }));
  };

  const validateStep1 = () => {
    const newErrors: Partial<Record<keyof IntakeForm, string>> = {};
    if (!form.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!form.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!form.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Enter a valid email';
    if (!form.businessName.trim()) newErrors.businessName = 'Business name is required';
    if (!form.businessType) newErrors.businessType = 'Please select a business type';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors: Partial<Record<keyof IntakeForm, string>> = {};
    if (form.services.length === 0) newErrors.services = 'Please select at least one service';
    if (!form.frequency) newErrors.frequency = 'Please select a frequency';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.consent) {
      setErrors({ consent: 'You must agree to proceed' });
      return;
    }
    setIsSubmitting(true);

    // Save to Supabase
    try {
      const supabase = createClient();
      await supabase.from('intake_submissions').insert({
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        phone: form.phone,
        business_name: form.businessName,
        business_type: form.businessType,
        services: form.services,
        frequency: form.frequency,
        current_software: form.currentSoftware,
        months_behind: form.monthsBehind,
        message: form.message,
        consent: form.consent,
        status: 'new',
      });
    } catch {
      // silently continue — also save to localStorage as fallback
    }

    // localStorage fallback
    const submission = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      ...form,
    };
    try {
      const existing = JSON.parse(localStorage.getItem('syhj_intake_submissions') || '[]');
      existing.unshift(submission);
      localStorage.setItem('syhj_intake_submissions', JSON.stringify(existing));
    } catch {
      // silently fail
    }

    // Send new_intake notification via /api/notify (admin + client alerts)
    fetch('/api/notify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event: 'new_intake',
        intake: {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          businessName: form.businessName,
          services: form.services,
          frequency: form.frequency,
          phone: form.phone,
          businessType: form.businessType,
        },
      }),
    }).catch(() => {
      // silently fail — notification is best-effort
    });

    setIsSubmitting(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <section id="portal" className="bg-brand-black py-16 sm:py-24 lg:py-32">
        <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-primary flex items-center justify-center mx-auto mb-6 sm:mb-8">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
                <path d="M6 16l7 7 13-13" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2
              className="text-white mb-4"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 'clamp(24px, 6vw, 44px)',
                fontWeight: 700,
                letterSpacing: '-1px',
              }}
            >
              Intake submitted successfully.
            </h2>
            <p
              className="text-white/65 text-sm sm:text-base mb-6"
              style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", lineHeight: '1.7' }}
            >
              Thank you, {form.firstName}. I&apos;ve received your intake form and will review your requirements within 1–2 business days. You&apos;ll receive a detailed scope and estimate at {form.email}.
            </p>
            <p className="text-white/40 text-sm mb-8" style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}>
              Track your engagement status in the{' '}
              <a href="/client-portal" className="text-[#fd5108] hover:underline font-semibold">Client Portal</a>
              {' '}— create an account using the same email address.
            </p>
            <button
              onClick={() => { setSubmitted(false); setForm(initialForm); setStep(1); }}
              className="text-white font-bold text-sm border-b border-primary pb-0.5 bg-transparent cursor-pointer touch-manipulation"
              style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
            >
              Submit another inquiry
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="portal" className="bg-brand-black py-16 sm:py-24 lg:py-32">
      <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16 lg:gap-24">
          {/* Left: Info */}
          <div>
            <p
              className="text-primary font-sans text-xs font-semibold tracking-widest uppercase mb-4"
              style={{ letterSpacing: '3px' }}
            >
              Secure Client Intake
            </p>
            <h2
              className="text-white mb-5 sm:mb-6"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 'clamp(26px, 6vw, 44px)',
                fontWeight: 700,
                lineHeight: '1.15',
                letterSpacing: '-1px',
              }}
            >
              Start your engagement
              <br />
              with confidence.
            </h2>
            <div className="w-12 h-0.5 bg-primary mb-6 sm:mb-8" />
            <p
              className="text-white/65 text-sm sm:text-base mb-8 sm:mb-10 leading-relaxed"
              style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", lineHeight: '1.75' }}
            >
              Complete the intake form to get started. Your information is handled with strict confidentiality. I&apos;ll review your submission and respond within 1–2 business days with a detailed scope and estimate.
            </p>

            {/* Security badges */}
            <div className="space-y-4 sm:space-y-5">
              {[
                {
                  icon: (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                      <path d="M10 2L3 5v5c0 4.5 3 8 7 9 4-1 7-4.5 7-9V5l-7-3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                      <path d="M7 10l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ),
                  title: 'Confidential Handling',
                  desc: 'All submitted information is treated as strictly confidential and used only for service delivery.',
                },
                {
                  icon: (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                      <rect x="3" y="8" width="14" height="10" rx="1" stroke="currentColor" strokeWidth="1.5" />
                      <path d="M7 8V6a3 3 0 016 0v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      <circle cx="10" cy="13" r="1.5" fill="currentColor" />
                    </svg>
                  ),
                  title: 'No Obligation',
                  desc: 'Submitting this form does not create a binding agreement. You review the scope before any commitment.',
                },
                {
                  icon: (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                      <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5" />
                      <path d="M10 6v4l2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  ),
                  title: '1–2 Business Day Response',
                  desc: 'You\'ll receive a detailed scope and estimate within 1–2 business days of submission.',
                },
              ].map((badge) => (
                <div key={badge.title} className="flex items-start gap-4">
                  <div className="w-10 h-10 border border-white/10 flex items-center justify-center flex-shrink-0 text-white/40">
                    {badge.icon}
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold mb-0.5"
                      style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}>
                      {badge.title}
                    </p>
                    <p className="text-white/40 text-xs leading-relaxed"
                      style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}>
                      {badge.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Step indicator */}
            <div className="mt-10 sm:mt-12 flex items-center gap-3 sm:gap-4">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center gap-2">
                  <div
                    className={`w-8 h-8 flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                      step === s
                        ? 'bg-primary text-white'
                        : step > s
                        ? 'bg-primary/30 text-primary' : 'bg-white/10 text-white/40'
                    }`}
                    style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
                  >
                    {step > s ? '✓' : s}
                  </div>
                  <span
                    className={`text-xs ${
                      step >= s ? 'text-white/70' : 'text-white/30'
                    }`}
                    style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
                  >
                    {s === 1 ? 'Contact' : s === 2 ? 'Services' : 'Details'}
                  </span>
                  {s < 3 && <div className="w-6 sm:w-8 h-px bg-white/15" />}
                </div>
              ))}
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-white p-5 sm:p-8 lg:p-10">
            <form onSubmit={handleSubmit} noValidate>
              {/* Step 1: Contact Info */}
              {step === 1 && (
                <div>
                  <h3
                    className="text-brand-dark mb-5 sm:mb-6"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '20px', fontWeight: 600 }}
                  >
                    Contact Information
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="label-field" style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}>
                        First Name <span className="text-primary">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.firstName}
                        onChange={(e) => handleChange('firstName', e.target.value)}
                        className={`input-field ${errors.firstName ? 'border-red-500' : ''}`}
                        placeholder="Jane"
                        style={inputStyle}
                        autoComplete="given-name"
                      />
                      {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                    </div>
                    <div>
                      <label className="label-field" style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}>
                        Last Name <span className="text-primary">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.lastName}
                        onChange={(e) => handleChange('lastName', e.target.value)}
                        className={`input-field ${errors.lastName ? 'border-red-500' : ''}`}
                        placeholder="Smith"
                        style={inputStyle}
                        autoComplete="family-name"
                      />
                      {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="label-field" style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}>
                      Email Address <span className="text-primary">*</span>
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className={`input-field ${errors.email ? 'border-red-500' : ''}`}
                      placeholder="jane@company.com"
                      style={inputStyle}
                      autoComplete="email"
                      inputMode="email"
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>

                  <div className="mb-4">
                    <label className="label-field" style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}>
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className="input-field"
                      placeholder="(555) 000-0000"
                      style={inputStyle}
                      autoComplete="tel"
                      inputMode="tel"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="label-field" style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}>
                      Business Name <span className="text-primary">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.businessName}
                      onChange={(e) => handleChange('businessName', e.target.value)}
                      className={`input-field ${errors.businessName ? 'border-red-500' : ''}`}
                      placeholder="Acme LLC"
                      style={inputStyle}
                      autoComplete="organization"
                    />
                    {errors.businessName && <p className="text-red-500 text-xs mt-1">{errors.businessName}</p>}
                  </div>

                  <div className="mb-6 sm:mb-8">
                    <label className="label-field" style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}>
                      Business Type <span className="text-primary">*</span>
                    </label>
                    <select
                      value={form.businessType}
                      onChange={(e) => handleChange('businessType', e.target.value)}
                      className={`input-field ${errors.businessType ? 'border-red-500' : ''}`}
                      style={selectStyle}
                    >
                      <option value="">Select business type</option>
                      {businessTypes.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                    {errors.businessType && <p className="text-red-500 text-xs mt-1">{errors.businessType}</p>}
                  </div>

                  <button
                    type="button"
                    onClick={handleNext}
                    className="w-full bg-primary text-white py-4 font-bold text-base hover:bg-primary-dark transition-all duration-300 cursor-pointer touch-manipulation"
                    style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", transitionTimingFunction: 'cubic-bezier(0.22, 0.61, 0.36, 1)', minHeight: '52px' }}
                  >
                    Continue to Services →
                  </button>
                </div>
              )}

              {/* Step 2: Services */}
              {step === 2 && (
                <div>
                  <h3
                    className="text-brand-dark mb-5 sm:mb-6"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '20px', fontWeight: 600 }}
                  >
                    Services Required
                  </h3>

                  <div className="mb-5 sm:mb-6">
                    <label className="label-field" style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}>
                      Select services needed <span className="text-primary">*</span>
                    </label>
                    <div className="space-y-2">
                      {serviceOptions.map((service) => (
                        <label
                          key={service}
                          className={`flex items-center gap-3 p-3 cursor-pointer border transition-all duration-200 touch-manipulation ${
                            form.services.includes(service)
                              ? 'border-primary bg-orange-50' : 'border-brand-grey200 hover:border-brand-grey300'
                          }`}
                          style={{ minHeight: '48px' }}
                        >
                          <input
                            type="checkbox"
                            checked={form.services.includes(service)}
                            onChange={() => handleServiceToggle(service)}
                            className="accent-primary w-5 h-5 flex-shrink-0"
                          />
                          <span
                            className="text-sm text-brand-dark"
                            style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
                          >
                            {service}
                          </span>
                        </label>
                      ))}
                    </div>
                    {errors.services && <p className="text-red-500 text-xs mt-1">{errors.services}</p>}
                  </div>

                  <div className="mb-5 sm:mb-6">
                    <label className="label-field" style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}>
                      Engagement frequency <span className="text-primary">*</span>
                    </label>
                    <select
                      value={form.frequency}
                      onChange={(e) => handleChange('frequency', e.target.value)}
                      className={`input-field ${errors.frequency ? 'border-red-500' : ''}`}
                      style={selectStyle}
                    >
                      <option value="">Select frequency</option>
                      <option value="monthly">Monthly (ongoing)</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="one-time">One-time project</option>
                    </select>
                    {errors.frequency && <p className="text-red-500 text-xs mt-1">{errors.frequency}</p>}
                  </div>

                  <div className="mb-6 sm:mb-8">
                    <label className="label-field" style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}>
                      Current accounting software
                    </label>
                    <select
                      value={form.currentSoftware}
                      onChange={(e) => handleChange('currentSoftware', e.target.value)}
                      className="input-field"
                      style={selectStyle}
                    >
                      <option value="">Select software</option>
                      {softwareOptions.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="flex-1 border border-brand-grey300 text-brand-dark py-4 font-bold text-base hover:border-brand-dark transition-all duration-300 cursor-pointer bg-white touch-manipulation"
                      style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", minHeight: '52px' }}
                    >
                      ← Back
                    </button>
                    <button
                      type="button"
                      onClick={handleNext}
                      className="flex-1 bg-primary text-white py-4 font-bold text-base hover:bg-primary-dark transition-all duration-300 cursor-pointer touch-manipulation"
                      style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", transitionTimingFunction: 'cubic-bezier(0.22, 0.61, 0.36, 1)', minHeight: '52px' }}
                    >
                      Continue →
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Additional Details */}
              {step === 3 && (
                <div>
                  <h3
                    className="text-brand-dark mb-5 sm:mb-6"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: '20px', fontWeight: 600 }}
                  >
                    Additional Details
                  </h3>

                  <div className="mb-5 sm:mb-6">
                    <label className="label-field" style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}>
                      Are books current or behind?
                    </label>
                    <select
                      value={form.monthsBehind}
                      onChange={(e) => handleChange('monthsBehind', e.target.value)}
                      className="input-field"
                      style={selectStyle}
                    >
                      <option value="">Select status</option>
                      <option value="current">Books are current</option>
                      <option value="1-3">1–3 months behind</option>
                      <option value="4-6">4–6 months behind</option>
                      <option value="7-12">7–12 months behind</option>
                      <option value="12+">More than 12 months behind</option>
                    </select>
                  </div>

                  <div className="mb-5 sm:mb-6">
                    <label className="label-field" style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}>
                      Additional notes or context
                    </label>
                    <textarea
                      value={form.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      rows={4}
                      className="input-field resize-none"
                      placeholder="Describe your situation, any specific requirements, or questions you have..."
                      style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", fontSize: '16px' }}
                    />
                  </div>

                  <div className="mb-6 sm:mb-8 p-4 bg-brand-grey50 border border-brand-grey200">
                    <label className="flex items-start gap-3 cursor-pointer touch-manipulation">
                      <input
                        type="checkbox"
                        checked={form.consent}
                        onChange={(e) => handleChange('consent', e.target.checked)}
                        className="accent-primary w-5 h-5 mt-0.5 flex-shrink-0"
                      />
                      <span
                        className="text-sm text-brand-grey700 leading-relaxed"
                        style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
                      >
                        I understand that submitting this form does not create a binding agreement. My information will be used solely to prepare a service scope and estimate, and will be handled with strict confidentiality.
                      </span>
                    </label>
                    {errors.consent && <p className="text-red-500 text-xs mt-2">{errors.consent}</p>}
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      className="flex-1 border border-brand-grey300 text-brand-dark py-4 font-bold text-base hover:border-brand-dark transition-all duration-300 cursor-pointer bg-white touch-manipulation"
                      style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", minHeight: '52px' }}
                    >
                      ← Back
                    </button>
                    <button
                      type="submit"
                      className="flex-1 bg-primary text-white py-4 font-bold text-base hover:bg-primary-dark transition-all duration-300 cursor-pointer touch-manipulation"
                      style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", transitionTimingFunction: 'cubic-bezier(0.22, 0.61, 0.36, 1)', minHeight: '52px' }}
                    >
                      Submit Intake Form
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
